/* eslint-disable jira/filename-case */
import type { TaskId } from './types.tsx';

const completeJwmTaskName = (taskId: TaskId) => `jwm-onboarding-checklist.${taskId}`;
const completeJswTaskName = (taskId: TaskId) => `jsw-onboarding-checklist.${taskId}`;
const completeJsmTaskName = (taskId: TaskId) => `jsm-onboarding-checklist.${taskId}`;
const completePlanTaskName = (taskId: TaskId) => `plan-onboarding-checklist.${taskId}`;

export const PACKAGE_NAME = 'jiraSoftwareOnboardingQuickstart';
export const PACKAGE_TEAM_NAME = 'jlove-makkuro';

export const DEFAULT_NUDGE_WIDTH = 320;

export const ROOT_TASK = 'root';

// Objects Stored in Backend & Local
export const QUICKSTART_TYPE = {
	JSM_BUSINESS_QUICKSTART_KEY: 'jsm-business-checklist',
	OLD_GLOBAL_QUICKSTART_KEY: 'jsw-onboarding-checklist',
} as const;
export type QUICKSTART_TYPE = (typeof QUICKSTART_TYPE)[keyof typeof QUICKSTART_TYPE];
// Generates backend property key for quickstart visibility
export const getBackendVisibilityKey = (type: QUICKSTART_TYPE) => `${type}.visibility`;

// Old backend property keys for quickstart visibility shared by all of Quickstart
export const KEY_OPEN = 'jsw-onboarding-checklist.open';
export const KEY_DISMISSED = 'jsw-onboarding-checklist.dismissed';

export const KEY_TASK_GROUP = 'jsw-onboarding-checklist.task-group';
export const KEY_OPEN_TASK = 'jsw-onboarding-checklist.open-task';
export const KEY_HAS_EVER_SEEN_ROUTE = 'jsw-onboarding-checklist.has-ever-seen-route';
export const KEY_PREMIUM_TRIAL_REOPENED = 'jsw-onboarding-checklist.reopened-on-premium-trial';

// Used for analytics event context for primary (drawer) view
export const ACTION_SUBJECT_ID = 'nuxOnboardingQuickstart';
export const SOURCE = 'nuxOnboardingQuickstart';

/**
 * JSW onboarding for setting user properties
 * Reference:
 * src/packages/software/onboarding/src/view/view.js
 */
export const KEY_JSW_ONBOARDING = 'jsw.onboarding';
export const convertToHaveSeenItTask = (taskId: TaskId) => `${taskId}.have-seenit`;

// @todo: Clean up and remove any reference to this const when productionising simplify_kanban_qs
export const IDENTIFY_SMALL_CHUNKS_OF_WORK_KANBAN_TASK: string = completeJswTaskName(
	'identify-small-chunks-of-work-kanban',
);

// Plan v2 tasks
export const PLAN_TEAM_CAPACITY_TASK: string = completePlanTaskName('team-capacity-task');
export const PLAN_PROGRAM_BOARD_TASK: string = completePlanTaskName('program-board-task');
export const PLAN_RELEASE_MANAGEMENT_TASK: string = completePlanTaskName('release-management-task');
export const PLAN_DEPENDENCY_TRACKING_TASK: string = completePlanTaskName(
	'dependency-tracking-task',
);

// JWM tasks
export const JWM_CREATE_PROJECT_TASK: string = completeJwmTaskName('jwm-create-project-task');
export const JWM_BUILD_FORM_TASK: string = completeJwmTaskName('build-form-task');
export const JWM_CALENDAR_TASK: string = completeJwmTaskName('calendar-task');
export const JWM_TIMELINE_TASK: string = completeJwmTaskName('timeline-task');
export const JWM_TRELLO_CROSSFLOW_TASK_IMPORT: string =
	completeJwmTaskName('trello-crossflow-import');
export const JWM_SUMMARY_AND_REPORTS_TASK: string = completeJwmTaskName('summary-and-reports-task');
export const JWM_TRELLO_CROSSFLOW_CALENDAR_AND_TIMELINE_TASK: string = completeJwmTaskName(
	'trello-crossflow-calendar-and-timeline',
);
export const JWM_TRELLO_CROSSFLOW_TASK_KEY_DIFFERENCES: string = completeJwmTaskName(
	'trello-crossflow-key-differences',
);

// definition comes from https://hello.atlassian.net/wiki/spaces/Spork/pages/3962753569/Tailored+Onboarding+-+Operations
export const OPS_JTBD_1_LIST: string = completeJwmTaskName('OPS_JTBD_1_LIST');
export const OPS_JTBD_2_BOARD: string = completeJwmTaskName('OPS_JTBD_2_BOARD');
export const OPS_JTBD_2_FORMS: string = completeJwmTaskName('OPS_JTBD_2_FORMS');
export const OPS_JTBD_2_SUMMARY: string = completeJwmTaskName('OPS_JTBD_2_SUMMARY');
export const OPS_JTBD_3_LIST: string = completeJwmTaskName('OPS_JTBD_3_LIST');
export const OPS_JTBD_3_CALENDAR: string = completeJwmTaskName('OPS_JTBD_3_CALENDAR');
export const OPS_JTBD_3_PAGES: string = completeJwmTaskName('OPS_JTBD_3_PAGES');
export const OPS_JTBD_4_LIST: string = completeJwmTaskName('OPS_JTBD_4_LIST');
export const OPS_JTBD_4_CALENDAR: string = completeJwmTaskName('OPS_JTBD_4_CALENDAR');
export const OPS_JTBD_4_TIMELINE: string = completeJwmTaskName('OPS_JTBD_4_TIMELINE');
export const OPS_JTBD_4_SUMMARY: string = completeJwmTaskName('OPS_JTBD_4_SUMMARY');
export const OPS_JTBD_5_LIST: string = completeJwmTaskName('OPS_JTBD_5_LIST');
export const OPS_JTBD_5_TIMELINE: string = completeJwmTaskName('OPS_JTBD_5_TIMELINE');
export const OPS_JTBD_5_PAGES: string = completeJwmTaskName('OPS_JTBD_5_PAGES');
export const OPS_JTBD_6_PAGES: string = completeJwmTaskName('OPS_JTBD_6_PAGES');
export const OPS_JTBD_7_LIST: string = completeJwmTaskName('OPS_JTBD_7_LIST');
export const OPS_JTBD_7_CALENDAR: string = completeJwmTaskName('OPS_JTBD_7_CALENDAR');
export const OPS_JTBD_0_LIST: string = completeJwmTaskName('OPS_JTBD_0_LIST');
export const OPS_JTBD_0_TIMELINE: string = completeJwmTaskName('OPS_JTBD_0_TIMELINE');
export const OPS_JTBD_0_PAGES: string = completeJwmTaskName('OPS_JTBD_0_PAGES');
export const OPS_JTBD_DEFAULT_LIST: string = completeJwmTaskName('OPS_JTBD_DEFAULT_LIST');
export const OPS_JTBD_DEFAULT_BOARD: string = completeJwmTaskName('OPS_JTBD_DEFAULT_BOARD');
export const OPS_JTBD_DEFAULT_INVITES: string = completeJwmTaskName('OPS_JTBD_DEFAULT_INVITES');

// definition comes from https://hello.atlassian.net/wiki/spaces/Spork/pages/3820424905/Tailored+Onboarding+-+Personalised+Landing+based+on+JTBD#Dynamic-Personalisation
export const MKT_JTBD_1_LIST: string = completeJwmTaskName('MKT_JTBD_1_LIST');
export const MKT_JTBD_1_CALENDAR: string = completeJwmTaskName('MKT_JTBD_1_CALENDAR');
export const MKT_JTBD_1_TIMELINE: string = completeJwmTaskName('MKT_JTBD_1_TIMELINE');
export const MKT_JTBD_2_CALENDAR: string = completeJwmTaskName('MKT_JTBD_2_CALENDAR');
export const MKT_JTBD_2_PAGES: string = completeJwmTaskName('MKT_JTBD_2_PAGES');
export const MKT_JTBD_3_PAGES: string = completeJwmTaskName('MKT_JTBD_3_PAGES');
export const MKT_JTBD_3_TIMELINE: string = completeJwmTaskName('MKT_JTBD_3_TIMELINE');
export const MKT_JTBD_4_LIST: string = completeJwmTaskName('MKT_JTBD_4_LIST');
export const MKT_JTBD_4_SUMMARY: string = completeJwmTaskName('MKT_JTBD_4_SUMMARY');
export const MKT_JTBD_4_PAGES: string = completeJwmTaskName('MKT_JTBD_4_PAGES');
export const MKT_JTBD_5_TIMELINE: string = completeJwmTaskName('MKT_JTBD_5_TIMELINE');
export const MKT_JTBD_5_SUMMARY: string = completeJwmTaskName('MKT_JTBD_5_SUMMARY');
export const MKT_JTBD_6_FORMS: string = completeJwmTaskName('MKT_JTBD_6_FORMS');
export const MKT_JTBD_0_LIST: string = completeJwmTaskName('MKT_JTBD_0_LIST');
export const MKT_JTBD_0_CALENDAR: string = completeJwmTaskName('MKT_JTBD_0_CALENDAR');
export const MKT_JTBD_0_TIMELINE: string = completeJwmTaskName('MKT_JTBD_0_TIMELINE');
export const MKT_JTBD_0_SUMMARY: string = completeJwmTaskName('MKT_JTBD_0_SUMMARY');
export const MKT_JTBD_DEFAULT_LIST: string = completeJwmTaskName('MKT_JTBD_DEFAULT_LIST');
export const MKT_JTBD_DEFAULT_BOARD: string = completeJwmTaskName('MKT_JTBD_DEFAULT_BOARD');
export const MKT_JTBD_DEFAULT_PROJECT: string = completeJwmTaskName('MKT_JTBD_DEFAULT_PROJECT');
export const MKT_JTBD_DEFAULT_INVITES: string = completeJwmTaskName('MKT_JTBD_DEFAULT_INVITES');

// tailored onboarding M3: Kanban tasks for non-software team type
export const TAILORED_ONBOARDING_SUMMARY: string = completeJswTaskName(
	'TAILORED_ONBOARDING_SUMMARY',
);
export const TAILORED_ONBOARDING_LIST: string = completeJswTaskName('TAILORED_ONBOARDING_LIST');
export const TAILORED_ONBOARDING_CALENDAR: string = completeJswTaskName(
	'TAILORED_ONBOARDING_CALENDAR',
);
export const TAILORED_ONBOARDING_BOARD: string = completeJswTaskName('TAILORED_ONBOARDING_BOARD');
export const TAILORED_ONBOARDING_TIMELINE: string = completeJswTaskName(
	'TAILORED_ONBOARDING_TIMELINE',
);
export const TAILORED_ONBOARDING_PAGES: string = completeJswTaskName('TAILORED_ONBOARDING_PAGES');
export const TAILORED_ONBOARDING_FORM: string = completeJswTaskName('TAILORED_ONBOARDING_FORM');
export const TAILORED_ONBOARDING_INVITE: string = completeJswTaskName('TAILORED_ONBOARDING_INVITE');
export const TAILORED_ONBOARDING_BACKLOG: string = completeJswTaskName(
	'TAILORED_ONBOARDING_BACKLOG',
);
export const TAILORED_ONBOARDING_CODE: string = completeJswTaskName('TAILORED_ONBOARDING_CODE');

// JSM tasks
export const JSM_CUSTOMIZE_YOUR_REQUEST_TYPES_TASK: string = completeJsmTaskName(
	'customize-your-request-types-task',
);
export const JSM_REVIEW_YOUR_CHANNELS_TASK: string = completeJsmTaskName(
	'review-your-channels-task',
);
export const JSM_INVITE_YOUR_TEAM_TASK: string = completeJsmTaskName('invite-your-team-task');

export const KEY_HAS_NAVIGATED_FROM_LINK = 'jsw-onboarding-checklist.has.navigated.from.link';
export const ACCOUNT_ID = 'accountId';

export const QUICKSTART_UI_STATUS = {
	NOT_LOADED: 'NOT_LOADED',
	OPEN: 'OPEN',
	MINIMIZED: 'MINIMIZED',
	DISMISSED: 'DISMISSED',
} as const;

export const BACKLOG_CREATE_SPRINT_NUDGE_ID = 'backlogCreateSprintNudge';

// Remove this when cleaning up feature gate jira_remove_quickstart_from_access_page
export const PROJECT_SETTING_PAGES: string[] = [
	'legacy-project-settings-people',
	'project-settings-software-access',
];

export const JSM_BUSINESS_TASKS: string[] = [
	JSM_REVIEW_YOUR_CHANNELS_TASK,
	JSM_CUSTOMIZE_YOUR_REQUEST_TYPES_TASK,
	JSM_INVITE_YOUR_TEAM_TASK,
];

// Right now we list all the available tasks manually.

export const allTasksExcludingJSMBusinessTasks: string[] = [
	IDENTIFY_SMALL_CHUNKS_OF_WORK_KANBAN_TASK,
	JWM_CREATE_PROJECT_TASK,
	JWM_BUILD_FORM_TASK,
	JWM_TRELLO_CROSSFLOW_TASK_IMPORT,
	JWM_SUMMARY_AND_REPORTS_TASK,
	JWM_TRELLO_CROSSFLOW_CALENDAR_AND_TIMELINE_TASK,
	JWM_TRELLO_CROSSFLOW_TASK_KEY_DIFFERENCES,
	OPS_JTBD_1_LIST,
	OPS_JTBD_2_BOARD,
	OPS_JTBD_2_FORMS,
	OPS_JTBD_2_SUMMARY,
	OPS_JTBD_3_LIST,
	OPS_JTBD_3_CALENDAR,
	OPS_JTBD_3_PAGES,
	OPS_JTBD_4_LIST,
	OPS_JTBD_4_CALENDAR,
	OPS_JTBD_4_TIMELINE,
	OPS_JTBD_4_SUMMARY,
	OPS_JTBD_5_LIST,
	OPS_JTBD_5_TIMELINE,
	OPS_JTBD_5_PAGES,
	OPS_JTBD_6_PAGES,
	OPS_JTBD_7_LIST,
	OPS_JTBD_7_CALENDAR,
	OPS_JTBD_0_LIST,
	OPS_JTBD_0_TIMELINE,
	OPS_JTBD_0_PAGES,
	OPS_JTBD_DEFAULT_LIST,
	OPS_JTBD_DEFAULT_BOARD,
	OPS_JTBD_DEFAULT_INVITES,
	MKT_JTBD_1_LIST,
	MKT_JTBD_1_CALENDAR,
	MKT_JTBD_1_TIMELINE,
	MKT_JTBD_2_CALENDAR,
	MKT_JTBD_2_PAGES,
	MKT_JTBD_3_PAGES,
	MKT_JTBD_3_TIMELINE,
	MKT_JTBD_4_LIST,
	MKT_JTBD_4_SUMMARY,
	MKT_JTBD_4_PAGES,
	MKT_JTBD_5_TIMELINE,
	MKT_JTBD_5_SUMMARY,
	MKT_JTBD_6_FORMS,
	MKT_JTBD_0_LIST,
	MKT_JTBD_0_CALENDAR,
	MKT_JTBD_0_TIMELINE,
	MKT_JTBD_0_SUMMARY,
	MKT_JTBD_DEFAULT_LIST,
	MKT_JTBD_DEFAULT_BOARD,
	MKT_JTBD_DEFAULT_PROJECT,
	MKT_JTBD_DEFAULT_INVITES,
	// Tailored onboarding M3
	TAILORED_ONBOARDING_SUMMARY,
	TAILORED_ONBOARDING_LIST,
	TAILORED_ONBOARDING_CALENDAR,
	TAILORED_ONBOARDING_BOARD,
	TAILORED_ONBOARDING_TIMELINE,
	TAILORED_ONBOARDING_PAGES,
	TAILORED_ONBOARDING_FORM,
	TAILORED_ONBOARDING_INVITE,
	TAILORED_ONBOARDING_BACKLOG,
	TAILORED_ONBOARDING_CODE,
	//
];

export const SAMPLE_DATA_PROJECT_KEYS_TO_HIDE_ON: string[] = ['SAM1'];
